import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import http from '../http';
import C from '../colors';
import { getSpaceUsedInUnit } from '../util';
import { formatMoney } from '../money';

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 13 4 4L19 7"/>
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none"
      style={{ animation: 'cdialog-spin 1s linear infinite' }}>
      <circle cx="18" cy="18" r="14" stroke={C.hair2} strokeWidth="3"/>
      <path d="M18 4a14 14 0 0 1 14 14" stroke={C.brand} strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}

function PlanCard({ title, tagline, badge, products, onPurchase, disabled, notice, extraFeatures = [] }) {
  return (
    <div style={{
      flex: 1,
      background: disabled ? C.paper2 : '#fff',
      border: `1px solid ${C.hair}`,
      borderRadius: 14,
      padding: '24px 24px 20px',
      display: 'flex', flexDirection: 'column',
      opacity: disabled ? 0.72 : 1,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {!disabled && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, ${C.brand}, ${C.brand2})`,
          borderRadius: '14px 14px 0 0',
        }}/>
      )}

      <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: C.serif, fontSize: 26, fontWeight: 400, color: C.ink }}>
          {title}
        </span>
        {badge && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
            padding: '2px 6px', borderRadius: 4,
            background: disabled ? C.hair2 : C.brand,
            color: disabled ? C.ink3 : '#fff',
          }}>
            {badge}
          </span>
        )}
      </div>

      <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20, fontFamily: C.sans }}>
        {tagline}
      </div>

      {notice && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: C.warningBg, borderRadius: 8,
          fontSize: 12, color: C.warningFg, fontFamily: C.sans, lineHeight: 1.5,
        }}>
          {notice}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {products.map((p) => (
          <div key={p?.id || p?.code} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px',
            background: disabled ? C.paper3 : C.paper2,
            borderRadius: 10,
            border: `1px solid ${C.hair}`,
          }}>
            <div>
              <div style={{ fontFamily: C.mono, fontSize: 20, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
                ¥{formatMoney(p?.price)}
                <span style={{ fontSize: 12, color: C.ink3, marginLeft: 4 }}>
                  / {p?.duration} 个月
                </span>
              </div>
              {p?.duration >= 12 && (
                <div style={{ fontSize: 11, color: C.brand2, marginTop: 2, fontFamily: C.sans }}>
                  年付更优惠
                </div>
              )}
            </div>
            <button
              disabled={disabled}
              onClick={() => onPurchase(p?.id)}
              data-testid={p?.code ? `subscription-buy-${p.code}` : undefined}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: disabled ? C.hair2 : C.ink,
                color: disabled ? C.ink3 : '#fff',
                fontFamily: C.sans, fontSize: 13, fontWeight: 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              订阅
            </button>
          </div>
        ))}
      </div>

      {(products[0]?.spaceLimit || products[0]?.maxDimension || extraFeatures.length > 0) && (
        <div style={{ marginTop: 'auto' }}>
          <div style={{
            fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4,
            textTransform: 'uppercase', color: C.ink3, marginBottom: 10,
          }}>
            包含权益
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {products[0]?.spaceLimit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.ink2, fontFamily: C.sans }}>
                <span style={{ color: C.brand }}><CheckIcon/></span>
                存储空间 {getSpaceUsedInUnit(products[0].spaceLimit)}
              </div>
            )}
            {products[0]?.maxDimension && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.ink2, fontFamily: C.sans }}>
                <span style={{ color: C.brand }}><CheckIcon/></span>
                最大尺寸 {products[0].maxDimension}
              </div>
            )}
            {extraFeatures.map((feat, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.ink2, fontFamily: C.sans }}>
                <span style={{ color: C.brand }}><CheckIcon/></span>
                {feat}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Waiting screen shown after payment window opened ───────────────
function WaitingScreen({ outTradeNo, onSuccess, onRetry, onCancel }) {
  const [status, setStatus] = useState('polling'); // 'polling' | 'checking' | 'success' | 'error'
  // Keep latest props in a ref so the interval closure is always up-to-date
  // without needing to restart the interval on every render.
  const refs = useRef({ outTradeNo, onSuccess });
  refs.current.outTradeNo = outTradeNo;
  refs.current.onSuccess = onSuccess;

  const poll = useCallback(async (manual = false) => {
    const tradeNo = refs.current.outTradeNo;
    if (!tradeNo) return;
    if (manual) setStatus('checking');
    try {
      const res = await http.get(`/user/subscription/query/${tradeNo}`);
      if (res.data?.booking?.status === 'completed') {
        setStatus('success');
        setTimeout(() => refs.current.onSuccess(), 1200);
      } else if (manual) {
        setStatus('polling');
      }
    } catch {
      if (manual) setStatus('error');
    }
  }, []); // stable — reads everything from refs, never needs to change

  // Start interval once on mount; cleanup on unmount.
  useEffect(() => {
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [poll]);

  const isSuccess = status === 'success';
  const isChecking = status === 'checking';

  return (
    <>
      <style>{`@keyframes cdialog-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        padding: '48px 40px 40px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center',
      }}>

        {/* Icon area */}
        <div style={{ marginBottom: 24, position: 'relative', width: 64, height: 64, display: 'grid', placeItems: 'center' }}>
          {isSuccess ? (
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: `${C.brand}20`,
              display: 'grid', placeItems: 'center',
              animation: 'none',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.brand} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 13 4 4L19 7"/>
              </svg>
            </div>
          ) : (
            <SpinnerIcon/>
          )}
        </div>

        {/* Heading */}
        <div style={{ fontFamily: C.serif, fontSize: 28, color: C.ink, marginBottom: 8, letterSpacing: -0.3 }}>
          {isSuccess ? '支付成功' : '等待支付'}
        </div>
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.ink3, lineHeight: 1.6, maxWidth: 280, marginBottom: 36 }}>
          {isSuccess
            ? '订阅已激活，正在刷新你的权益…'
            : '支付宝窗口已打开，请在其中完成付款。\n完成后点击「我已支付」。'}
        </div>

        {/* Buttons */}
        {!isSuccess && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 280 }}>
            <button
              onClick={() => poll(false)}
              disabled={isChecking}
              style={{
                height: 46, borderRadius: 12, border: 'none',
                background: isChecking ? C.hair2 : C.brand,
                color: isChecking ? C.ink3 : '#fff',
                fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                cursor: isChecking ? 'default' : 'pointer',
                boxShadow: isChecking ? 'none' : '0 8px 24px -8px rgba(247,119,176,.5)',
                transition: 'background .15s',
              }}
            >
              {isChecking ? '验证中…' : '我已支付'}
            </button>
            <button
              onClick={onRetry}
              style={{
                height: 46, borderRadius: 12,
                border: `1.5px solid ${C.hair}`,
                background: '#fff', color: C.ink2,
                fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              重新发起支付
            </button>
          </div>
        )}

        {/* Cancel link */}
        {!isSuccess && (
          <button
            onClick={onCancel}
            style={{
              marginTop: 20, background: 'none', border: 'none',
              fontFamily: C.sans, fontSize: 13, color: C.ink3,
              cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
            }}
          >
            取消订阅
          </button>
        )}
      </div>
    </>
  );
}

const ChargeDialog = inject('chargeStore', 'userStore')(observer(({ userStore, chargeStore }) => {
  const [member, setMember] = useState(null);
  const [memberAnnual, setMemberAnnual] = useState(null);
  const [vip, setVip] = useState(null);
  const [vipAnnual, setVipAnnual] = useState(null);
  // pending = { outTradeNo, productId } while waiting for payment
  const [pending, setPending] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!chargeStore.chargeDialogOpen) { setPending(null); return; }
    http.get('/user/products').then((res) => {
      const products = res.data.products || [];
      setMember(products.find(p => p.code === 'TUPAI_1_MONTH_MEMBERSHIP'));
      setMemberAnnual(products.find(p => p.code === 'TUPAI_12_MONTH_MEMBERSHIP'));
      setVip(products.find(p => p.code === 'TUPAI_1_MONTH_VIP_MEMBERSHIP'));
      setVipAnnual(products.find(p => p.code === 'TUPAI_12_MONTH_VIP_MEMBERSHIP'));
    }).catch(() => {});
  }, [chargeStore.chargeDialogOpen]);

  const openPayWindow = useCallback((productId) => {
    // Open synchronously while the click is still a user gesture. Opening it
    // after the API request resolves is commonly blocked by popup protection,
    // leaving a pending booking without a visible payment page.
    const newWin = window.open('', '_blank');
    if (!newWin) {
      return Promise.reject(new Error('Payment window was blocked'));
    }
    return http.post('/user/subscription', { productId, payType: 'alipay' }).then((response) => {
      newWin.document.open();
      newWin.document.write(response.data.alipay);
      newWin.document.close();
      return response.data.outTradeNo || response.data.out_trade_no || null;
    }).catch((error) => {
      newWin.close();
      throw error;
    });
  }, []);

  const purchase = useCallback((productId) => {
    openPayWindow(productId).then((outTradeNo) => {
      setPending({ outTradeNo, productId });
    }).catch(console.error);
  }, [openPayWindow]);

  const handleRetry = useCallback(() => {
    if (!pending?.productId) return;
    openPayWindow(pending.productId).then((outTradeNo) => {
      setPending({ outTradeNo, productId: pending.productId });
    }).catch(console.error);
  }, [pending, openPayWindow]);

  const handleSuccess = useCallback(() => {
    userStore.refreshInfo();
    chargeStore.closeChargeDialog();
  }, [userStore, chargeStore]);

  const goToSubscriptions = useCallback(() => {
    chargeStore.closeChargeDialog();
    navigate('/user/info/subscriptions');
  }, [navigate, chargeStore]);

  const goToInvoice = useCallback(() => {
    chargeStore.closeChargeDialog();
    navigate('/user/info/invoice');
  }, [navigate, chargeStore]);

  if (!chargeStore.chargeDialogOpen) return null;

  return (
    <div
      data-testid="subscription-dialog-backdrop"
      onClick={() => { if (!pending) chargeStore.closeChargeDialog(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        background: 'rgba(26,20,23,.55)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        data-testid="subscription-dialog"
        onClick={e => e.stopPropagation()}
        style={{
          background: C.paper,
          borderRadius: 18,
          border: `1px solid ${C.hair}`,
          width: '100%',
          maxWidth: pending ? 420 : 720,
          boxShadow: '0 24px 60px -12px rgba(0,0,0,.35)',
          overflow: 'hidden',
          transition: 'max-width .25s ease',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 18px',
          borderBottom: `1px solid ${C.hair}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{
              fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4,
              textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 4,
            }}>
              Subscription
            </div>
            <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 26, fontWeight: 400, color: C.ink }}>
              {pending ? '完成支付' : '订阅续费'}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!pending && <>
              <button onClick={goToSubscriptions} style={{
                background: 'none', border: `1px solid ${C.hair2}`,
                color: C.ink2, padding: '6px 12px', borderRadius: 7,
                fontFamily: C.sans, fontSize: 12, cursor: 'pointer',
              }}>
                订阅记录
              </button>
              <button onClick={goToInvoice} style={{
                background: 'none', border: `1px solid ${C.hair2}`,
                color: C.ink2, padding: '6px 12px', borderRadius: 7,
                fontFamily: C.sans, fontSize: 12, cursor: 'pointer',
              }}>
                发票管理
              </button>
            </>}
            {!pending && (
              <button onClick={() => chargeStore.closeChargeDialog()} style={{
                width: 32, height: 32, borderRadius: 8,
                border: `1px solid ${C.hair2}`,
                background: '#fff', color: C.ink2,
                cursor: 'pointer', display: 'grid', placeItems: 'center',
              }}>
                <CloseIcon/>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {pending ? (
          <WaitingScreen
            outTradeNo={pending.outTradeNo}
            onSuccess={handleSuccess}
            onRetry={handleRetry}
            onCancel={() => { setPending(null); chargeStore.closeChargeDialog(); }}
          />
        ) : (
          <div style={{ padding: '20px 24px 24px', display: 'flex', gap: 16 }}>
            <PlanCard
              title="会员"
              tagline="适合个人摄影师"
              badge="推荐"
              products={[member, memberAnnual].filter(Boolean)}
              onPurchase={purchase}
            />
            <PlanCard
              title="高级会员"
              tagline="适合工作室 / 团队"
              products={[vip, vipAnnual].filter(Boolean)}
              onPurchase={purchase}
              extraFeatures={['短信选片通知', 'RAW 文件解析']}
            />
          </div>
        )}
      </div>
    </div>
  );
}));

export default ChargeDialog;
